import "./DownloadFilesDrawer.css";

import { datasetToTitleCase, humanFileSize } from "../../services/utils";

import { FILE_TYPE_LABEL } from "../../constants";
import { InlineSpinner } from "../InlineSpinner";
import PropTypes from "prop-types";
import React from "react";
import { CfSelect as Select } from "../CfSelect";
import moment from "moment";
import useDownloadFiles from "../../hooks/useDownloadFiles";

const DownloadFilesDrawer = ({
  statistics,
  uploadSession,
  onClose,
  user,
  fixed = false,
}) => {
  const {
    downloadableStats,
    selectedCategory,
    files,
    loading,
    checkedIds,
    downloading,
    checkedFiles,
    allChecked,
    someChecked,
    hasSelection,
    handleCategoryChange,
    handleToggle,
    handleCheckAll,
    handleDownloadSelection,
  } = useDownloadFiles(statistics, uploadSession, user);

  if (!uploadSession || downloadableStats.length === 0) return null;

  const categoryOptions = downloadableStats.map((stat) => ({
    value: stat.category,
    label: datasetToTitleCase(FILE_TYPE_LABEL[stat.category] ?? stat.category),
  }));

  return (
    <div
      className={`dl-drawer-overlay${fixed ? " dl-drawer-overlay--fixed" : ""}`}
    >
      <div className="dl-drawer-header d-flex align-items-center justify-content-between px-3 py-2 gap-2">
        <span className="dl-drawer-title d-flex align-items-center gap-2">
          <i className="fa-solid fa-download" />
          Download files
        </span>
        <button
          className="dl-drawer-close btn btn-link p-0"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="dl-drawer-body p-3 d-flex flex-column gap-3">
        <div>
          <div className="dl-select-label mb-1">Category</div>
          <Select
            className="cf-rs"
            classNamePrefix="cf-rs"
            options={categoryOptions}
            value={
              categoryOptions.find(
                (option) => option.value === selectedCategory
              ) ?? null
            }
            onChange={(option) => handleCategoryChange(option.value)}
            // .dl-drawer-body scrolls, which would clip the open menu the way
            // the native popup never was. Fixed keeps the menu inside .cf-rs,
            // so its styling still applies (a portal would lose it).
            menuPosition="fixed"
            placeholder="Select a category"
            aria-label="Category"
          />
        </div>

        <hr className="my-0" />

        <div>
          {loading && (
            <div className="text-center py-3">
              <InlineSpinner className="me-2" />
              Loading…
            </div>
          )}
          {!loading && !selectedCategory && (
            <div className="text-center text-muted py-3">
              Select a category to see the files.
            </div>
          )}
          {!loading && selectedCategory && (
            <>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="dl-files-count">
                  {files.length} file{files.length > 1 ? "s" : ""}
                </span>
                {files.length > 1 && (
                  <button
                    className="dl-check-all btn btn-link p-0 d-flex align-items-center gap-1"
                    onClick={handleCheckAll}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someChecked;
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    Select all
                  </button>
                )}
              </div>

              <div className="d-flex flex-column gap-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className={`dl-file-card d-flex align-items-center gap-2 p-2${
                      checkedIds[file.id] ? " checked" : ""
                    }`}
                    onClick={() => handleToggle(file.id)}
                  >
                    <div className="dl-file-icon-wrap d-flex align-items-center justify-content-center">
                      <i
                        className={
                          checkedIds[file.id]
                            ? "fa-solid fa-check"
                            : "fa-regular fa-file"
                        }
                      />
                    </div>
                    <div className="dl-file-card-info flex-fill overflow-hidden">
                      <div className="dl-file-card-name mb-1">
                        {file.filename}
                      </div>
                      <div className="dl-file-card-meta d-flex gap-2">
                        <span className="d-flex align-items-center gap-1">
                          <i className="fa-regular fa-hard-drive" />
                          {humanFileSize(file.size)}
                        </span>
                        <span className="d-flex align-items-center gap-1">
                          <i className="fa-regular fa-clock" />
                          {moment(file.updated_at).format("YYYY-MM-DD HH:mm")}
                        </span>
                      </div>
                    </div>
                    <a
                      href={file.download_url}
                      download={file.filename}
                      className="dl-download-icon-btn d-flex align-items-center justify-content-center text-decoration-none"
                      onClick={(e) => e.stopPropagation()}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Download this file"
                    >
                      <i className="fa-solid fa-download" />
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {hasSelection && (
        <div className="dl-sticky-bar d-flex align-items-center justify-content-between px-3 py-2 gap-3">
          <span className="dl-sticky-label">
            {checkedFiles.length} file{checkedFiles.length > 1 ? "s" : ""}{" "}
            selected
          </span>
          <button
            className="btn btn-success dl-sticky-dl-btn d-flex align-items-center gap-2 px-3 py-2"
            onClick={handleDownloadSelection}
            disabled={downloading}
          >
            {downloading ? (
              <InlineSpinner />
            ) : (
              <i className="fa-solid fa-download" />
            )}
            {downloading ? "Downloading…" : `Download (${checkedFiles.length})`}
          </button>
        </div>
      )}

      <div className="dl-drawer-footer border-top p-2">
        <button
          className="dl-close-btn btn btn-outline-secondary btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

DownloadFilesDrawer.propTypes = {
  statistics: PropTypes.array.isRequired,
  uploadSession: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  fixed: PropTypes.bool,
};

export default DownloadFilesDrawer;
